/**
 * CopilotPanel — the audit's expert assistant, docked beside the workspace.
 *
 * Ask anything about this audit — the copilot reads the audit's own record
 * (criteria results, evidence, notes and ingested emails, the data request,
 * the linked work-plan activity) and answers in plain words with concrete
 * next steps. Strictly user-triggered; every exchange is kept as reviewable
 * history.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

const STARTERS = {
  setup: ['What should I confirm before sending the engagement letter?', 'Are the authorities on this audit complete?'],
  engagement: ['Summarize the data request in plain words', 'What deadlines does the auditee need to hit?'],
  data_integrity: ['Explain the universe validation result', 'Is this universe fit for fieldwork?'],
  fieldwork: ['Which conditions still need my review?', 'Is the evidence sufficient for the high-severity conditions?'],
  reporting: ['Draft the finding language for the deficiencies', 'What should go in the executive summary?'],
  closed: ['What should we watch in the CAP follow-up?', 'What would make next year\'s audit smoother?'],
};

export default function CopilotPanel({ auditId, stage }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [convId, setConvId] = useState(null);
  const [steps, setSteps] = useState([]);
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/copilot`, { credentials: 'include' }).then(r => r.json());
      if (r.success && r.messages?.length) {
        setMessages(r.messages.map(m => ({ role: m.role, content: m.content })));
        setConvId(r.messages[r.messages.length - 1].conversation_id);
      }
    } catch { /* fresh conversation */ }
  }, [auditId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages, busy]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput(''); setSteps([]);
    setMessages(m => [...m, { role: 'user', content: q }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/copilot`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, conversation_id: convId }),
      }).then(r => r.json());
      if (!r.success) throw new Error(r.message || 'No answer');
      setConvId(r.conversation_id);
      setMessages(m => [...m, { role: 'assistant', content: r.reply }]);
      setSteps(r.suggested_next_steps || []);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: `Sorry — ${e.message}. Try again.` }]);
    }
    setBusy(false);
  };

  const starters = STARTERS[stage] || STARTERS.setup;

  return (
    <div className="bg-white border border-slate-200 rounded-xl flex flex-col h-full max-h-[640px]">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-xs font-bold">✦</div>
        <div>
          <div className="text-sm font-semibold text-slate-800">Copilot</div>
          <div className="text-[10px] text-slate-400">Knows this audit inside out. Ask anything.</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[220px]">
        {!messages.length && (
          <div className="space-y-2">
            <div className="text-[12px] text-slate-400">A few things people ask at this point:</div>
            {starters.map((s, i) => (
              <button key={i} onClick={() => ask(s)} className="block w-full text-left text-[12px] text-slate-600 bg-slate-50 hover:bg-orange-50 hover:text-orange-700 border border-slate-200 rounded-lg px-3 py-2">{s}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : ''}>
            <div className={`max-w-[92%] rounded-xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-orange-600 text-white' : 'bg-slate-50 text-slate-700 border border-slate-100'}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-[12px] text-slate-400">
            <span className="inline-block w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
            Reading the audit record…
          </div>
        )}
        {!busy && steps.length > 0 && (
          <div className="border-t border-slate-100 pt-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Suggested next steps</div>
            {steps.map((s, i) => <div key={i} className="text-[12px] text-slate-600">→ {s}</div>)}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-slate-100 flex gap-2 flex-shrink-0">
        <input
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && ask()}
          placeholder="Ask about this audit…"
          className="flex-1 text-[13px] border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400"
          disabled={busy}
        />
        <button onClick={() => ask()} disabled={busy || !input.trim()} className="px-3 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-40">Send</button>
      </div>
    </div>
  );
}
